import React, { useState, useContext } from "react";
import { i18n } from "../../translate/i18n";
import { 
    Avatar, 
    CardHeader, 
    Grid,
    Dialog,
    DialogContent,
    Tooltip,
    useMediaQuery,
    IconButton,
    Typography,
    Popover,
    Box,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import CloseIcon from "@material-ui/icons/Close";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useModernLayout } from "../../context/ModernLayout/ModernLayoutContext";
import { resolveContactAvatar, hasCustomAvatar } from "../../utils/avatar";

const useStyles = makeStyles((theme) => ({
    // Estilos para o modal da imagem
    imageModal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    imageModalContent: {
        outline: "none",
        maxWidth: "90vw",
        maxHeight: "90vh",
    },
    expandedImage: {
        width: "100%",
        height: "auto",
        maxWidth: "500px",
        borderRadius: theme.spacing(1),
    },
    clickableAvatar: {
        cursor: "pointer",
        "&:hover": {
            opacity: 0.8,
        },
    },
    cardHeader: {
        padding: '4px 0',
        flex: 1,
        minWidth: 0,
        '& .MuiCardHeader-content': {
            overflow: 'hidden',
            minWidth: 0,
            flex: 1,
        },
        '& .MuiCardHeader-title': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '0.875rem',
            fontWeight: 500,
            maxWidth: '100%',
        },
        '& .MuiCardHeader-subheader': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '0.75rem',
            maxWidth: '100%',
        },
        '& .MuiCardHeader-avatar': {
            marginRight: 8,
        },
    },
    compactWrapper: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        width: '100%',
    },
    moreButton: {
        padding: 2,
        marginLeft: 2,
        minWidth: 'auto',
        flexShrink: 0,
    },
    infoPopover: {
        padding: theme.spacing(2),
        maxWidth: 300,
    },
    infoTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
    infoSubtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
        lineHeight: 1.4,
    },
}));

const TicketInfo = ({ contact, ticket, onClick, compactDesktop = false }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isModernLayout = useModernLayout();
    const [amount, setAmount] = useState("");
    const { user } = useContext(AuthContext);
    const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem
    const [infoAnchorEl, setInfoAnchorEl] = useState(null); // Estado para o popover de info
    
    // Aplicar estilos compactos apenas no Modern Layout + Mobile
    const useCompactStyle = isMobile && isModernLayout;
    
    // Handlers para o popover de informações
    const handleInfoClick = (event) => {
        event.stopPropagation();
        setInfoAnchorEl(event.currentTarget);
    };
    
    const handleInfoClose = () => {
        setInfoAnchorEl(null);
    };
    
    const infoPopoverOpen = Boolean(infoAnchorEl);

    // Função para abrir modal da imagem
    const handleImageClick = (e) => {
        e.stopPropagation(); // Prevenir que o clique no avatar execute outros handlers
        // Meow/WuzAPI grava em profilePicUrl; Baileys costuma usar urlPicture — ambos no util
        if (contact && hasCustomAvatar(contact)) {
            setImageModalOpen(true);
        }
    };

    // Função para fechar modal da imagem
    const handleImageModalClose = () => {
        setImageModalOpen(false);
    };

    const renderCardReader = () => {
        // Texto completo do título
        const fullTitle = `${contact?.name || '(sem contato)'} #${ticket?.id}`;
        const contactName = contact?.name || "(sem contato)";
        const compactTitle = (() => {
            const maxChars = 22;
            return contactName.length > maxChars ? `${contactName.substring(0, maxChars)}...` : contactName;
        })();
        const compactDesktopTitle = (() => {
            const maxChars = 32;
            const reducedName =
                contactName.length > maxChars
                    ? `${contactName.substring(0, maxChars)}...`
                    : contactName;
            return `${reducedName} #${ticket?.id}`;
        })();
        
        // Texto completo do subtítulo
        const subheaderParts = [
            ticket?.user && `${i18n.t("messagesList.header.assignedTo")} ${ticket?.user?.name}`,
            contact?.contactWallets && contact.contactWallets.length > 0
                ? `• ${i18n.t("wallets.wallet")}: ${contact.contactWallets[0].wallet?.name || 'N/A'}`
                : null
        ].filter(Boolean);
        const fullSubheader = subheaderParts.join(' ');

        // Layout compacto apenas para Modern Layout + Mobile
        if (useCompactStyle) {
            return (
                <div className={classes.compactWrapper}>
                    <CardHeader
                        onClick={onClick}
                        style={{ cursor: "pointer", flex: 1, minWidth: 0 }}
                        className={classes.cardHeader}
                        titleTypographyProps={{ noWrap: true }}
                        subheaderTypographyProps={{ noWrap: true }}
                        avatar={
                            <Avatar 
                                src={resolveContactAvatar(contact)} 
                                alt="contact_image" 
                                className={classes.clickableAvatar}
                                onClick={handleImageClick}
                                style={{ width: 36, height: 36 }}
                            />
                        }
                        title={compactTitle}
                        subheader={fullSubheader}
                    />
                    <IconButton 
                        className={classes.moreButton}
                        onClick={handleInfoClick}
                        size="small"
                        title="Ver informações completas"
                    >
                        <MoreHorizIcon fontSize="small" />
                    </IconButton>
                    
                    {/* Popover com informações completas */}
                    <Popover
                        open={infoPopoverOpen}
                        anchorEl={infoAnchorEl}
                        onClose={handleInfoClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        <Box className={classes.infoPopover}>
                            <Box display="flex" alignItems="center" marginBottom={1}>
                                <Avatar 
                                    src={resolveContactAvatar(contact)} 
                                    alt="contact_image"
                                    style={{ width: 48, height: 48, marginRight: 12 }}
                                    onClick={handleImageClick}
                                    className={classes.clickableAvatar}
                                />
                                <Box>
                                    <Typography className={classes.infoTitle}>
                                        {contact?.name || '(sem contato)'}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Ticket #{ticket?.id}
                                    </Typography>
                                </Box>
                            </Box>
                            {fullSubheader && (
                                <Typography className={classes.infoSubtitle}>
                                    {fullSubheader}
                                </Typography>
                            )}
                            {contact?.number && (
                                <Typography className={classes.infoSubtitle} style={{ marginTop: 4 }}>
                                    📱 {contact.number}
                                </Typography>
                            )}
                        </Box>
                    </Popover>
                </div>
            );
        }

        // Layout padrão (Classic e Desktop)
        return (
            <CardHeader
                onClick={onClick}
                style={{ cursor: "pointer", flex: 1, minWidth: 0 }}
                className={classes.cardHeader}
                titleTypographyProps={{
                    noWrap: true,
                    style: compactDesktop ? { fontSize: "0.8rem" } : undefined,
                }}
                subheaderTypographyProps={{
                    noWrap: true,
                    style: compactDesktop ? { fontSize: "0.68rem" } : undefined,
                }}
                avatar={
                    <Avatar 
                        src={resolveContactAvatar(contact)} 
                        alt="contact_image" 
                        className={classes.clickableAvatar}
                        onClick={handleImageClick}
                        style={compactDesktop ? { width: 34, height: 34 } : undefined}
                    />
                }
                title={compactDesktop ? compactDesktopTitle : fullTitle}
                subheader={fullSubheader}
            />
        );
    }

    const handleChange = (event) => {
        const value = event.target.value;
        setAmount(value);
    }

    return (
        <React.Fragment>
            <Grid container alignItems="center" spacing={useCompactStyle ? 0 : 1}>
                <Grid item xs={12}>
                    {renderCardReader()}
                </Grid>
            </Grid>

            {/* Modal da Imagem */}
            <Dialog
                open={imageModalOpen}
                onClose={handleImageModalClose}
                className={classes.imageModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent className={classes.imageModalContent}>
                    <img 
                        src={resolveContactAvatar(contact)} 
                        alt={contact?.name || "Foto do contato"}
                        className={classes.expandedImage}
                    />
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
};

export default TicketInfo;